/**
 *
 * @module widevineDrmProvider
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/drm.md
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/drm.md#widevine
 *
 */

import Check from 'check-types';
import Logging from './../logging/entry';
import Services from './../services/entry';
import Token from './../token/entry';
import DrmProvider from './drmProvider';
import DrmManager from './drmManager';
import WidevineCertificateStorage from './widevineCertificateStorage';

const Util = Services.Util;
const TokenManager = Token.TokenManager;
const DrmClientEndpoint = Services.Drm.DrmClientEndpoint;
const DrmType = Services.Media.DrmType;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict
} = Check.assert;

/**
 *
 * @class WidevineDrmProvider
 * @desc Widevine is Google's content encryption standard.
 * @since 3.2.0
 *
 */
export default class WidevineDrmProvider extends DrmProvider {

    /**
     *
     * @param {SDK.Drm.DrmManager} provider
     * @param {SDK.Token.TokenManager} tokenManager
     * @param {SDK.Logging.Logger} [logger=Logging]
     *
     */
    constructor(provider, tokenManager, logger = Logging) {

        const errorMessage = {
            ctor: 'WidevineDrmProvider(provider, tokenManager, logger)',
            get provider() {
                return `${this.ctor} needs valid provider SDK.Drm.DrmManager instance`;
            },
            get tokenManager() {
                return `${this.ctor} needs SDK.Token.TokenManager instance`;
            }
        };

        instanceStrict(provider, DrmManager, errorMessage.provider, InvalidArgumentException);
        instanceStrict(tokenManager, TokenManager, errorMessage.tokenManager, InvalidArgumentException);

        super(provider, tokenManager, logger);

        /**
         *
         * @access private
         * @type {String}
         *
         */
        this.licenseRequestUri = Util.getSafe(() => {
            return provider.config.client.endpoints[DrmClientEndpoint.widevineLicense].href;
        }, '');

        /**
         *
         * @access private
         * @type {Object}
         *
         */
        this.licenseRequestHeaders = Util.getSafe(() => {
            return provider.config.client.endpoints[DrmClientEndpoint.widevineLicense].headers;
        }, {});

        /**
         *
         * @access private
         * @type {String}
         *
         */
        this.certificateUri = Util.getSafe(() => {
            return provider.config.client.endpoints[DrmClientEndpoint.widevineCertificate].href;
        }, '');

        /**
         *
         * @access private
         * @type {Object}
         *
         */
        this.certificateHeaders = Util.getSafe(() => {
            return provider.config.client.endpoints[DrmClientEndpoint.widevineCertificate].headers;
        }, {});

        /**
         *
         * @access private
         * @type {String}
         * @desc DRM identifier String
         *
         */
        this.type = DrmType.WIDEVINE;

        /**
         *
         * @access private
         * @type {WidevineCertificateStorage}
         * @desc references the Storage API to get/set a certificate
         *
         */
        this.certificateStorage = new WidevineCertificateStorage(
            Util.getSafe(() => this.provider.clientId),
            Util.getSafe(() => this.provider.environment),
            Util.getSafe(() => this.provider.storage),
            this.logger
        );

        this.logger.log(WidevineDrmProvider.name, 'Created.');

    }

    /**
     *
     * @access public
     * @param {Array<Byte>} buffer
     * @desc Gets a license required for decrypting Widevine protected content.
     * @emits {MediaFailure} - Raised when the method fails, describing the cause of the problem.
     * @returns {Promise<Array<Byte>>}
     *
     */
    getWidevineLicense(buffer) {

        const errorMessage = {
            method: 'WidevineDrmProvider.getWidevineLicense(buffer)',
            get buffer() {
                return `${this.method} buffer should be an ArrayBuffer view`;
            }
        };

        Check.assert(ArrayBuffer.isView(buffer), errorMessage.buffer, InvalidArgumentException);

        const { provider, logger, failureEvent } = this;

        return provider.getWidevineLicense(buffer).then((data) => {

            return Promise.resolve(data);

        }).catch((exception) => {

            logger.error(WidevineDrmProvider.name, 'Widevine license request failure.');
            logger.info(WidevineDrmProvider.name, `Dispatch ${failureEvent} event.`);

            this.emit(failureEvent, exception);

            return Promise.reject(exception);

        });

    }

    /**
     *
     * @access public
     * @desc Gets a certificate required for decrypting Widevine protected content. First we attempt to retreive the
     * certificate from storage, if that does not exist we make a new request for a new certificate, store that and
     * then return the certificate.
     * @emits {MediaFailure} - Raised when the method fails, describing the cause of the problem.
     * @returns {Promise<Array<Byte>>}
     *
     */
    getWidevineCertificate() {

        const { provider, logger, failureEvent, certificateStorage } = this;

        return certificateStorage.getStoredCertificate().then((storedCertificate) => {

            if (Check.assigned(storedCertificate)) {

                return Promise.resolve(storedCertificate);

            } else {

                return provider.getWidevineCertificate().then((certificate) => {

                    return certificateStorage.storeCertificate(certificate).then(() => {
                        return certificateStorage.getStoredCertificate();
                    });

                }).catch((exception) => {

                    logger.error(WidevineDrmProvider.name, 'Widevine certificate request failure.');
                    logger.info(WidevineDrmProvider.name, `Dispatch ${failureEvent} event.`);

                    this.emit(failureEvent, exception);

                    return Promise.reject(exception);

                });
            }

        });

    }

}



// WEBPACK FOOTER //
// ./src/drm/widevineDrmProvider.js